import type { DockPanel } from "../../../../stores/dock";
import { parseTaskUrlId, taskUrlId } from "../../../../lib/routes";

/**
 * The inspector kind's payload (2026-08-28, owner directive: "contact info
 * is a kind of inspector that shall reside as other elements there" — item
 * inspectors are DOCK panels, not page-local asides).
 *
 * `inspector` names WHICH inspector renders the panel (the kind's Body
 * dispatches on it — see index.tsx). `props` is that inspector's own
 * payload, opaque to everyone else. The first registered inspector is
 * `contact`; a future merchant/listing/entity inspector is a new entry in
 * the Body's map plus a panel builder here — the dock host stays untouched
 * (the registry's founding contract).
 */
export interface InspectorData {
	inspector: string;
	props: Record<string, unknown>;
}

/** Panel ids <-> URL values, so a page can keep its inspector in the URL
 *  (hooks/useInspectorUrl) without re-deriving the id format the builders
 *  below own. */
export const contactPanelId = (slug: string) => `contact:${slug}`;
export function contactSlugOfPanel(panel: DockPanel): string | null {
	return panel.id.startsWith("contact:") ? panel.id.slice("contact:".length) : null;
}
export const projectPanelId = (slug: string) => `project:${slug}`;
export function projectSlugOfPanel(panel: DockPanel): string | null {
	return panel.id.startsWith("project:") ? panel.id.slice("project:".length) : null;
}
export const taskPanelId = (project: string, slug: string) => `task:${JSON.stringify([project, slug])}`;
/** A task panel's URL value (`project/slug`), or null for any other panel. */
export function taskUrlIdOfPanel(panel: DockPanel): string | null {
	if (!panel.id.startsWith("task:")) return null;
	try {
		const parsed: unknown = JSON.parse(panel.id.slice("task:".length));
		if (Array.isArray(parsed) && typeof parsed[0] === "string" && typeof parsed[1] === "string") {
			return taskUrlId(parsed[0], parsed[1]);
		}
	} catch {
		/* not a task panel id */
	}
	return null;
}
export function taskPanelIdFromUrl(value: string): string {
	const { project, slug } = parseTaskUrlId(value);
	return taskPanelId(project, slug);
}

export function projectInspectorPanel(slug: string, title: string): DockPanel {
  return { id: projectPanelId(slug), kind: "inspector", title, scope: "projects",
    data: { inspector: "project", props: { slug } } satisfies InspectorData };
}

/** No module scope: the same task can be inspected from Tasks or Projects. */
export function taskInspectorPanel(task: { slug: string; project: string; title: string }): DockPanel {
  return { id: taskPanelId(task.project, task.slug), kind: "inspector", title: task.title,
    data: { inspector: "task", props: { slug: task.slug, project: task.project } } satisfies InspectorData };
}

/** Read an inspector panel's payload. Total by construction — `data` is
 *  `unknown` on DockPanel, so exactly one place narrows it. */
export function inspectorData(panel: DockPanel): InspectorData {
	const d = panel.data as Partial<InspectorData> | undefined;
	return {
		inspector: d?.inspector ?? "",
		props: (d?.props as Record<string, unknown> | undefined) ?? {},
	};
}

/** Build the dock panel for one contact card. The id is stable per card, so
 *  clicking the same card focuses its existing tab instead of cloning
 *  (openPanel's add-or-focus contract); different cards get their own tabs
 *  (compare two people side by side), bounded by MAX_TABS eviction. */
export function contactInspectorPanel(slug: string, title: string): DockPanel {
	return {
		id: contactPanelId(slug),
		kind: "inspector",
		title,
		data: { inspector: "contact", props: { slug } } satisfies InspectorData,
		// Contacts' inspectors belong to the contacts module (owner rule:
		// modules show their own panels).
		scope: "contacts",
	};
}
